/**
 * Color-based Business Card Detection
 * Uses color segmentation and connected components for card detection
 */

export class ColorSegmentationDetector {
  constructor() {
    this.debugMode = false;
  }

  /**
   * Detect card using color segmentation
   */
  async detectCard(image) {
    console.log('Starting color segmentation detection');
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Step 1: Reduce colors using k-means clustering
    const segments = this.kMeansSegmentation(imageData, 5);
    
    // Step 2: Find the largest light-colored segment
    const cardSegment = this.findCardSegment(segments, imageData);
    
    if (!cardSegment) {
      console.log('No card segment found');
      return null;
    }
    
    // Step 3: Find connected components in the segment
    const component = this.findLargestComponent(cardSegment);
    
    if (!component) {
      console.log('No valid component found');
      return null;
    }
    
    // Step 4: Fit a rectangle to the component
    const corners = this.fitRectangle(component);
    
    return corners;
  }

  /**
   * K-means color clustering
   */
  kMeansSegmentation(imageData, k = 5) {
    const { data, width, height } = imageData;
    const pixels = [];
    
    // Sample pixels for faster clustering
    const sampleStep = 4;
    for (let i = 0; i < data.length; i += 4 * sampleStep) {
      pixels.push([data[i], data[i + 1], data[i + 2]]);
    }
    
    // Initialize centroids using k-means++
    const centroids = this.initializeCentroids(pixels, k);
    
    // Run k-means iterations
    for (let iter = 0; iter < 10; iter++) {
      // Assign pixels to clusters
      const clusters = Array(k).fill(null).map(() => []);
      
      for (const pixel of pixels) {
        let minDist = Infinity;
        let bestCluster = 0;
        
        for (let c = 0; c < k; c++) {
          const dist = this.colorDistance(pixel, centroids[c]);
          if (dist < minDist) {
            minDist = dist;
            bestCluster = c;
          }
        }
        
        clusters[bestCluster].push(pixel);
      }
      
      // Update centroids
      for (let c = 0; c < k; c++) {
        if (clusters[c].length > 0) {
          const sum = [0, 0, 0];
          for (const pixel of clusters[c]) {
            sum[0] += pixel[0];
            sum[1] += pixel[1];
            sum[2] += pixel[2];
          }
          centroids[c] = [
            sum[0] / clusters[c].length,
            sum[1] / clusters[c].length,
            sum[2] / clusters[c].length
          ];
        }
      }
    }
    
    // Create segmentation map
    const segments = new Uint8Array(width * height);
    
    for (let i = 0; i < data.length; i += 4) {
      const pixel = [data[i], data[i + 1], data[i + 2]];
      let minDist = Infinity;
      let bestCluster = 0;
      
      for (let c = 0; c < k; c++) {
        const dist = this.colorDistance(pixel, centroids[c]);
        if (dist < minDist) {
          minDist = dist;
          bestCluster = c;
        }
      }
      
      segments[i / 4] = bestCluster;
    }
    
    return { segments, centroids, width, height };
  }

  /**
   * Initialize centroids using k-means++
   */
  initializeCentroids(pixels, k) {
    const centroids = [];
    
    // Choose first centroid randomly
    centroids.push(pixels[Math.floor(Math.random() * pixels.length)]);
    
    // Choose remaining centroids
    for (let c = 1; c < k; c++) {
      const distances = pixels.map(pixel => {
        let minDist = Infinity;
        for (const centroid of centroids) {
          minDist = Math.min(minDist, this.colorDistance(pixel, centroid));
        }
        return minDist;
      });
      
      // Choose pixel with probability proportional to squared distance
      const sumDist = distances.reduce((a, b) => a + b * b, 0);
      let random = Math.random() * sumDist;
      
      for (let i = 0; i < pixels.length; i++) {
        random -= distances[i] * distances[i];
        if (random <= 0) {
          centroids.push(pixels[i]);
          break;
        }
      }
    }
    
    return centroids;
  }

  /**
   * Calculate color distance
   */
  colorDistance(c1, c2) {
    const dr = c1[0] - c2[0];
    const dg = c1[1] - c2[1];
    const db = c1[2] - c2[2];
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }

  /**
   * Find the segment most likely to be a business card
   */
  findCardSegment(segmentData, imageData) {
    const { segments, centroids, width, height } = segmentData;
    const { data } = imageData;
    
    // Calculate segment properties
    const segmentInfo = centroids.map((centroid, idx) => {
      let count = 0;
      let sumX = 0, sumY = 0;
      let minX = width, maxX = 0;
      let minY = height, maxY = 0;
      
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          if (segments[y * width + x] === idx) {
            count++;
            sumX += x;
            sumY += y;
            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y);
          }
        }
      }
      
      const brightness = (centroid[0] + centroid[1] + centroid[2]) / 3;
      const centerX = sumX / count;
      const centerY = sumY / count;
      const area = count;
      const boundingArea = (maxX - minX) * (maxY - minY);
      const fillRatio = area / boundingArea;
      
      return {
        idx,
        brightness,
        area,
        fillRatio,
        centerX,
        centerY,
        minX,
        maxX,
        minY,
        maxY
      };
    });
    
    // Find segment most likely to be a business card
    // Cards are typically bright, large, and rectangular (high fill ratio)
    let bestSegment = null;
    let bestScore = 0;
    
    for (const segment of segmentInfo) {
      // Skip if too small or too large
      const areaRatio = segment.area / (width * height);
      if (areaRatio < 0.1 || areaRatio > 0.9) continue;
      
      // Score based on brightness, size, and rectangularity
      const brightnessScore = segment.brightness / 255;
      const sizeScore = Math.min(1, areaRatio / 0.5);
      const rectangularityScore = segment.fillRatio;
      
      const score = brightnessScore * 0.4 + sizeScore * 0.3 + rectangularityScore * 0.3;
      
      if (score > bestScore) {
        bestScore = score;
        bestSegment = segment;
      }
    }
    
    if (!bestSegment) return null;
    
    // Create binary mask for the selected segment
    const mask = new Uint8Array(width * height);
    for (let i = 0; i < segments.length; i++) {
      mask[i] = segments[i] === bestSegment.idx ? 255 : 0;
    }
    
    return { mask, width, height, segment: bestSegment };
  }

  /**
   * Find largest connected component
   */
  findLargestComponent(cardSegment) {
    const { mask, width, height } = cardSegment;
    const visited = new Uint8Array(width * height);
    let largestComponent = null;
    let largestSize = 0;
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x;
        if (mask[idx] > 0 && !visited[idx]) {
          const component = this.floodFill(mask, visited, x, y, width, height);
          if (component.pixels > largestSize) {
            largestSize = component.pixels;
            largestComponent = component;
          }
        }
      }
    }
    
    return largestComponent;
  }

  /**
   * Flood fill to extract connected component
   */
  floodFill(mask, visited, startX, startY, width, height) {
    const stack = [{x: startX, y: startY}];
    const component = {
      pixels: 0,
      points: [],
      minX: startX,
      maxX: startX,
      minY: startY,
      maxY: startY
    };
    
    while (stack.length > 0) {
      const {x, y} = stack.pop();
      const idx = y * width + x;
      
      if (x < 0 || x >= width || y < 0 || y >= height || visited[idx] || mask[idx] === 0) {
        continue;
      }
      
      visited[idx] = 1;
      component.pixels++;
      component.points.push({x, y});
      component.minX = Math.min(component.minX, x);
      component.maxX = Math.max(component.maxX, x);
      component.minY = Math.min(component.minY, y);
      component.maxY = Math.max(component.maxY, y);
      
      // Add neighbors
      stack.push({x: x + 1, y});
      stack.push({x: x - 1, y});
      stack.push({x, y: y + 1});
      stack.push({x, y: y - 1});
    }
    
    return component;
  }

  /**
   * Fit rectangle to component using minimum area rectangle
   */
  fitRectangle(component) {
    const { minX, maxX, minY, maxY, points } = component;
    
    // Simple approach: use convex hull and minimum area rectangle
    // For now, just use bounding box with some refinement
    
    // Refine bounds by removing outliers
    if (points.length > 100) {
      // Sort points by x and y
      const sortedX = points.map(p => p.x).sort((a, b) => a - b);
      const sortedY = points.map(p => p.y).sort((a, b) => a - b);
      
      // Remove 5% outliers from each side
      const trimCount = Math.floor(points.length * 0.05);
      const refinedMinX = sortedX[trimCount];
      const refinedMaxX = sortedX[sortedX.length - trimCount - 1];
      const refinedMinY = sortedY[trimCount];
      const refinedMaxY = sortedY[sortedY.length - trimCount - 1];
      
      return [
        { x: refinedMinX, y: refinedMinY },
        { x: refinedMaxX, y: refinedMinY },
        { x: refinedMaxX, y: refinedMaxY },
        { x: refinedMinX, y: refinedMaxY }
      ];
    }
    
    // Use simple bounding box
    return [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY }
    ];
  }
}

// Export singleton instance
export const colorDetector = new ColorSegmentationDetector();